import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Icon } from '@/icons/Icon'
import { Loader } from '@/components/Loader'

import { message, pGet, pPatch, pPost, type OrgAdmin, type OrgRow } from './api'
import { generateHandoverPassword, hashFor } from './handover'
import { HandoverPassword } from './HandoverPassword'

/** One carrier: what it is configured as, who administers it, and the two switches. */
export default function OrganizationPage() {
  const { orgId = '' } = useParams()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const org = useQuery({
    queryKey: ['pf', 'org', orgId],
    queryFn: () => pGet<OrgRow>(`/organizations/${orgId}`),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pf', 'org', orgId] })
    void qc.invalidateQueries({ queryKey: ['pf', 'orgs'] })
  }

  if (org.isLoading) return <Loader size="md" label="Loading the organization" />
  if (org.isError || !org.data) {
    return (
      <div className="ix-alert ix-alert--error" role="alert">
        <div>{message(org.error, 'That organization could not be loaded.')}</div>
      </div>
    )
  }

  const o = org.data

  return (
    <>
      <div className="pf-head">
        <div>
          <Link className="pf-back" to="/admin/organizations">
            <Icon name="chevron-left" size={13} />
            Organizations
          </Link>
          <div className="pf-orghead">
            <span className="pf-orghead__mark" aria-hidden="true">
              {o.name.slice(0, 2).toUpperCase()}
            </span>
            <div>
              <h1 className="pf-orghead__n">
                {o.name}
                {o.isActive ? (
                  <span className="ix-chip ix-chip--success">Live</span>
                ) : (
                  <span className="ix-chip ix-chip--error">Off</span>
                )}
              </h1>
              <div className="pf-orghead__meta">
                <Icon name="globe" size={13} />
                <a href={o.baseUrl} target="_blank" rel="noopener noreferrer">
                  {o.baseUrl.replace(/^https?:\/\//, '')}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="pf-head__acts">
          <Switch org={o} onChanged={invalidate} />
        </div>
      </div>

      <section className="st-panel pf-sec">
        <header className="st-panel__head">
          <div className="st-panel__t">On record</div>
        </header>
        <div className="st-panel__body">
          <div className="pf-facts">
            <Fact k="Mail domain" v={o.domain} />
            <Fact k="Domain name" v={o.domainName} />
            <Fact k="Subdomain" v={o.subdomain} />
            <Fact k="People" v={`${o.activeUsers} of ${o.users}`} />
            <Fact k="Circulars" v={String(o.circulars)} />
            <Fact
              k="Onboarding"
              v={o.onboardingCompletedAt ? new Date(o.onboardingCompletedAt).toLocaleDateString() : 'Not finished'}
            />
            <Fact k="Created" v={new Date(o.createdAt).toLocaleDateString()} />
            <Fact k="Switched off" v={o.deactivatedAt ? new Date(o.deactivatedAt).toLocaleString() : null} />
          </div>
        </div>
      </section>

      <EditOrganization org={o} onSaved={invalidate} />
      <Administrators org={o} onChanged={invalidate} />
      <DeleteOrganization org={o} onDeleted={() => navigate('/admin/organizations', { replace: true })} />
    </>
  )
}

/**
 * One fact, and a comma-separated list is one fact per line.
 *
 * `arivonix.com,insuronix.com` has no space in it, so a narrow column broke it
 * mid-domain - "arivonix.com,insuronix.c / om", which reads as a typo rather
 * than as a value that ran out of room. Splitting on the commas it already has
 * breaks it where it means to.
 */
function Fact({ k, v }: { k: string; v: string | null }) {
  const lines = (v ?? '').split(',').map((s) => s.trim()).filter(Boolean)
  return (
    <div>
      <div className="pf-fact__k">{k}</div>
      <div className="pf-fact__v">
        {lines.length === 0 ? (
          <span className="st-muted">—</span>
        ) : (
          lines.map((line) => <span key={line}>{line}</span>)
        )}
      </div>
    </div>
  )
}

function Switch({ org, onChanged }: { org: OrgRow; onChanged: () => void }) {
  const toggle = useMutation({
    mutationFn: () => pPost<OrgRow>(`/organizations/${org.id}/${org.isActive ? 'deactivate' : 'activate'}`),
    onSuccess: onChanged,
  })

  // Switching a carrier off refuses every sign-in for everybody there, so it
  // must not be drawn as the page's primary action. It had `ix-btn--secondary
  // ix-btn--compact`, and compact's background wins on source order - so the
  // button came out solid primary blue with a warning triangle inside it,
  // saying "do this" and "careful" at once.
  return (
    <div style={{ textAlign: 'right' }}>
      <button
        className={org.isActive ? 'pf-switch pf-switch--off' : 'ix-btn ix-btn--compact'}
        type="button"
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        <Icon name={org.isActive ? 'alert-triangle' : 'check'} size={14} />
        {toggle.isPending ? 'Saving…' : org.isActive ? 'Switch off' : 'Switch on'}
      </button>
      {toggle.error ? <div className="ix-error-text">{message(toggle.error)}</div> : null}
    </div>
  )
}

function EditOrganization({ org, onSaved }: { org: OrgRow; onSaved: () => void }) {
  const [name, setName] = useState(org.name)
  const [domain, setDomain] = useState(org.domain ?? '')
  const [subdomain, setSubdomain] = useState(org.subdomain ?? '')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setName(org.name)
    setDomain(org.domain ?? '')
    setSubdomain(org.subdomain ?? '')
  }, [org.id, org.name, org.domain, org.subdomain])

  const save = useMutation({
    mutationFn: () =>
      pPatch<OrgRow>(`/organizations/${org.id}`, {
        name: name.trim(),
        domain: domain.trim(),
        subdomain: subdomain.trim(),
      }),
    onSuccess: () => {
      setSaved(true)
      onSaved()
    },
  })

  const dirty =
    name.trim() !== org.name ||
    domain.trim() !== (org.domain ?? '') ||
    subdomain.trim() !== (org.subdomain ?? '')

  const movingHost = subdomain.trim() !== (org.subdomain ?? '') && (org.subdomain ?? '') !== ''

  return (
    <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
      <header className="st-panel__head">
        <div className="st-panel__t">Edit</div>
      </header>
      <form
        className="st-panel__body"
        onSubmit={(e) => {
          e.preventDefault()
          if (save.isPending || !dirty) return
          setSaved(false)
          save.mutate()
        }}
      >
        <div className="st-grid">
          <label className="st-field">
            <span>Carrier name</span>
            <input className="ix-input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="st-field">
            <span>Mail domain</span>
            <input
              className="ix-input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              autoCapitalize="off"
              spellCheck={false}
            />
            <small>Decides who may be invited and who may sign in.</small>
          </label>
          <label className="st-field">
            <span>Subdomain</span>
            <input
              className="ix-input"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              autoCapitalize="off"
              spellCheck={false}
            />
            {movingHost ? <small>Moves their public address.</small> : null}
          </label>
        </div>

        {save.error ? (
          <div className="ix-alert ix-alert--error" role="alert" style={{ marginTop: 14 }}>
            <div>{message(save.error, 'Could not save.')}</div>
          </div>
        ) : null}
        {saved && !dirty ? (
          <div className="ix-alert ix-alert--success" role="status" style={{ marginTop: 14 }}>
            <div>Saved.</div>
          </div>
        ) : null}

        <div className="st-form__actions">
          <button className="ix-btn ix-btn--primary" type="submit" disabled={save.isPending || !dirty}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </section>
  )
}

function Administrators({ org, onChanged }: { org: OrgRow; onChanged: () => void }) {
  const [adding, setAdding] = useState(false)
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<'owner' | 'admin'>('owner')
  const [handover, setHandover] = useState<{ who: string; password: string } | null>(null)

  const add = useMutation({
    mutationFn: async () => {
      const addr = email.trim().toLowerCase()
      const password = generateHandoverPassword()
      await pPost<OrgRow>(`/organizations/${org.id}/admins`, {
        email: addr,
        fullName: fullName.trim(),
        role,
        password: await hashFor(password, addr),
      })
      return { who: addr, password }
    },
    onSuccess: (result) => {
      setHandover(result)
      setAdding(false)
      setEmail('')
      setFullName('')
      onChanged()
    },
  })

  const reset = useMutation({
    mutationFn: async (admin: OrgAdmin) => {
      const password = generateHandoverPassword()
      await pPost<OrgRow>(`/organizations/${org.id}/admins/${admin.id}/password`, {
        password: await hashFor(password, admin.email),
      })
      return { who: admin.email, password }
    },
    onSuccess: (result) => {
      setHandover(result)
      onChanged()
    },
  })

  const setActive = useMutation({
    mutationFn: ({ admin, isActive }: { admin: OrgAdmin; isActive: boolean }) =>
      pPatch<OrgRow>(`/organizations/${org.id}/admins/${admin.id}`, { isActive }),
    onSuccess: onChanged,
  })

  const error = add.error ?? reset.error ?? setActive.error

  return (
    <section className="st-panel pf-sec" style={{ marginBottom: 18 }}>
      <header className="st-panel__head">
        <div>
          <div className="st-panel__t">Owners and administrators</div>
          <div className="st-panel__d">The portal manages these accounts only.</div>
        </div>
        <button className="ix-btn ix-btn--compact" type="button" onClick={() => setAdding((v) => !v)}>
          <Icon name={adding ? 'chevron-right' : 'plus'} size={14} />
          {adding ? 'Cancel' : 'Add'}
        </button>
      </header>

      {adding ? (
        <form
          className="st-panel__body"
          style={{ borderBottom: '1px solid var(--ix-border)' }}
          onSubmit={(e) => {
            e.preventDefault()
            if (add.isPending || !email.trim() || !fullName.trim()) return
            add.mutate()
          }}
        >
          <div className="st-grid">
            <label className="st-field">
              <span>Email</span>
              <input
                className="ix-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <small>Must carry {org.domainName || 'the carrier’s domain'}.</small>
            </label>
            <label className="st-field">
              <span>Name</span>
              <input
                className="ix-input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </label>
            <label className="st-field">
              <span>Role</span>
              <select
                className="ix-select"
                value={role}
                onChange={(e) => setRole(e.target.value as 'owner' | 'admin')}
              >
                <option value="owner">Owner</option>
                <option value="admin">Administrator</option>
              </select>
            </label>
          </div>
          <div className="st-form__actions">
            <button className="ix-btn ix-btn--primary" type="submit" disabled={add.isPending}>
              {add.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      ) : null}

      {handover ? (
        <div className="st-panel__body" style={{ borderBottom: '1px solid var(--ix-border)' }}>
          <HandoverPassword who={handover.who} password={handover.password} />
          <div className="st-form__actions">
            <button className="ix-btn ix-btn--secondary" type="button" onClick={() => setHandover(null)}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="st-panel__body" style={{ paddingBottom: 0 }}>
          <div className="ix-alert ix-alert--error" role="alert">
            <div>{message(error)}</div>
          </div>
        </div>
      ) : null}

      <div className="st-panel__body st-panel__body--flush">
        <table className="ix-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Role</th>
              <th>Last signed in</th>
              <th>Status</th>
              <th className="pf-num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {org.admins.length === 0 ? (
              <tr>
                <td colSpan={5} className="pf-empty">
                  <span className="pf-empty__t">No owner or administrator</span>
                  Nobody there can invite anybody until one is added.
                </td>
              </tr>
            ) : (
              org.admins.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="st-user">
                      <span className="ix-avatar ix-avatar--sm">
                        {a.fullName.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="st-user__id">
                        <span className="st-user__n">{a.fullName}</span>
                        <span className="st-user__e">{a.email}</span>
                      </span>
                    </div>
                  </td>
                  <td className="pf-nowrap">{a.role === 'owner' ? 'Owner' : 'Administrator'}</td>
                  <td className="pf-nowrap">
                    {a.lastLoginAt ? (
                      new Date(a.lastLoginAt).toLocaleDateString()
                    ) : (
                      <span className="st-muted">Never</span>
                    )}
                  </td>
                  <td>
                    {!a.isActive ? (
                      <span className="ix-chip ix-chip--error">Deactivated</span>
                    ) : a.mustChangePassword ? (
                      <span className="ix-chip ix-chip--warning">Temporary</span>
                    ) : (
                      <span className="ix-chip ix-chip--success">Active</span>
                    )}
                  </td>
                  <td>
                    <div className="pf-rowacts">
                      <button
                        className="pf-act"
                        type="button"
                        disabled={reset.isPending}
                        onClick={() => reset.mutate(a)}
                      >
                        <Icon name="key" size={13} />
                        New password
                      </button>
                      <button
                        className="pf-act"
                        type="button"
                        disabled={setActive.isPending}
                        onClick={() => setActive.mutate({ admin: a, isActive: !a.isActive })}
                      >
                        {a.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function DeleteOrganization({ org, onDeleted }: { org: OrgRow; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState('')

  const remove = useMutation({
    mutationFn: () =>
      pPost<{ deleted: boolean }>(`/organizations/${org.id}/delete`, { confirmName: confirm.trim() }),
    onSuccess: onDeleted,
  })

  return (
    <section className="st-panel pf-sec pf-danger" style={{ marginBottom: 32 }}>
      <header className="st-panel__head">
        <div>
          <div className="st-panel__t">Delete</div>
          <div className="st-panel__d">
            {org.users} {org.users === 1 ? 'account' : 'accounts'} and {org.circulars}{' '}
            {org.circulars === 1 ? 'circular' : 'circulars'} go with it. Not recoverable.
          </div>
        </div>
      </header>
      <div className="st-panel__body">
        {org.isActive ? (
          <div className="ix-alert ix-alert--warning" role="status">
            <div>Switch it off first.</div>
          </div>
        ) : (
          <>
            <label className="st-field" style={{ maxWidth: 380 }}>
              <span>Type “{org.name}” to confirm</span>
              <input
                className="ix-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={org.name}
                autoComplete="off"
              />
            </label>
            {remove.error ? (
              <div className="ix-alert ix-alert--error" role="alert" style={{ marginTop: 12 }}>
                <div>{message(remove.error, 'Could not delete.')}</div>
              </div>
            ) : null}
            <div className="st-form__actions">
              <button
                className="st-act st-act--danger"
                type="button"
                disabled={remove.isPending || confirm.trim().toLowerCase() !== org.name.toLowerCase()}
                onClick={() => remove.mutate()}
              >
                <Icon name="trash-2" size={14} />
                {remove.isPending ? 'Deleting…' : `Delete ${org.name}`}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
